// One preview: /growth/previews/:id (docs/pages/ops-growth.md › Preview sites).
// A preview is a private ScoreLM league on its own address, built for one lead:
// they sign in with their personal link (the {{preview_url}} of an email) and see
// the site and the admin as a customer would. It deletes itself at its end.
// Here: Open (a super-admin look, without the lead's link), Copy sign-in link,
// Extend, Convert to real league, Delete now. An ended or deleted preview stays
// as the lead's history; a converted one points at its tenant.

import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '@/api'
import type { Preview } from '@/api/types'
import { PageHeader } from '@/components/shell/PageHeader'
import { Button, Card, CardHeader, ConfirmDialog, CopyButton, ErrorState, InlineAlert, KeyValueList, LoadingState, TrashIcon, useToast } from '@/components/ui'
import { formatDate } from '@/lib/format'
import { useDocumentTitle } from '@/lib/hooks'
import { addressText, daysLeftText } from '@/lib/previews'
import { invalidateQueries, setQueryData, useQuery } from '@/lib/query'
import { DaysDialog, OpenPreviewButton, PreviewStatusBadge } from './previewShared'

const TEMPLATE_LABELS: Record<string, string> = { classic: 'Classic', arena: 'Arena', huddle: 'Huddle' }

type DialogName = 'extend' | 'convert' | 'delete'

export default function PreviewDetailPage() {
  const id = Number(useParams().id)
  const toast = useToast()
  const { data: preview, error, status, refetch } = useQuery(['growth', 'preview', id], (signal) => api.growth.previews.get(id, signal))
  const [dialog, setDialog] = useState<DialogName | null>(null)
  useDocumentTitle(preview?.name ?? 'Preview')

  if (status === 'loading' && !preview) return <LoadingState label="Loading preview…" />
  if (!preview) {
    return (
      <Card>
        <ErrorState error={error} title="Couldn't load this preview" onRetry={refetch} />
      </Card>
    )
  }

  const apply = (next: Preview, message: string) => {
    setQueryData(['growth', 'preview', id], next)
    invalidateQueries('growth')
    toast.success(message)
  }
  const live = preview.status === 'active'
  const converted = preview.status === 'claimed'
  const leagueStillThere = !converted && preview.league_id !== null
  const who = preview.access_email ?? 'the lead'

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        crumbs={[{ label: 'Previews', to: '/growth/previews' }, { label: preview.name }]}
        title={preview.name}
        subtitle={addressText(preview)}
        badges={<PreviewStatusBadge preview={preview} />}
        actions={
          <div className="flex flex-wrap gap-2">
            {live ? <OpenPreviewButton preview={preview} /> : null}
            {live ? <Button onClick={() => setDialog('extend')}>Extend…</Button> : null}
            {live ? (
              <Button variant="primary" onClick={() => setDialog('convert')}>
                Convert to real league…
              </Button>
            ) : null}
            {leagueStillThere ? (
              <Button icon={<TrashIcon size={15} />} onClick={() => setDialog('delete')}>
                Delete now…
              </Button>
            ) : null}
          </div>
        }
      />

      {converted ? (
        <InlineAlert tone="good" title="It's a real league now">
          Converted {preview.converted_at ? formatDate(preview.converted_at) : ''}, on a trial. Its lead signs in with {who}.{' '}
          {preview.league_id ? (
            <Link to={`/tenants/${preview.league_id}`} className="font-semibold underline underline-offset-2">
              Open in Tenants
            </Link>
          ) : null}
        </InlineAlert>
      ) : !live ? (
        <InlineAlert title={preview.status === 'removed' ? 'Deleted' : 'Ended'}>
          {preview.league_id
            ? 'Its site goes within a few minutes.'
            : `Its site and everything in it were deleted${preview.league_deleted_at ? ` ${formatDate(preview.league_deleted_at)}` : ''}. This record stays on the lead's history, and the lead can have a new preview.`}
        </InlineAlert>
      ) : null}

      <div className="grid items-start gap-4 lg:grid-cols-[1.2fr_1fr]">
        <div className="grid content-start gap-4">
          <Card>
            <CardHeader title="Address" description="Private: anyone else who visits gets a sign-in page." />
            <p className="break-all rounded-md border border-line bg-raised/40 px-3 py-2 font-mono text-[12.5px] text-ink">{addressText(preview)}</p>
          </Card>

          {live && preview.sign_in_url ? (
            <Card>
              <CardHeader
                title="Sign-in link"
                description={`Personal to ${who}: it signs them straight in, to the site and the admin, until the preview ends. It's what {{preview_url}} puts in an email. Don't post it anywhere public.`}
              />
              <p className="break-all rounded-md border border-line bg-raised/40 px-3 py-2 font-mono text-[12.5px] text-ink">{preview.sign_in_url}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <CopyButton value={preview.sign_in_url} label="Copy sign-in link" ariaLabel="Copy the lead's sign-in link" />
              </div>
            </Card>
          ) : null}
        </div>

        <Card>
          <CardHeader title="Details" />
          <KeyValueList
            items={[
              { label: 'Lead', value: preview.lead ? <Link to={`/growth/leads/${preview.lead.id}`} className="underline underline-offset-2 hover:text-accent">{preview.lead.name}</Link> : 'Deleted' },
              { label: 'Signs in', value: who },
              { label: live ? 'Ends' : 'Ended', value: formatDate(preview.expires_at) },
              { label: 'Days left', value: daysLeftText(preview) },
              { label: 'Sign-ins', value: preview.view_count },
              { label: 'Last signed in', value: preview.last_viewed_at ? formatDate(preview.last_viewed_at) : 'Never' },
              { label: 'Teams', value: preview.team_count },
              { label: 'Games', value: preview.game_count },
              { label: 'Sport', value: preview.sport },
              { label: 'Template', value: TEMPLATE_LABELS[preview.template] ?? preview.template },
              { label: 'Built', value: formatDate(preview.created_at) },
            ]}
          />
        </Card>
      </div>

      <DaysDialog
        open={dialog === 'extend'}
        onClose={() => setDialog(null)}
        title="Extend this preview"
        description="The days are added to its end date. The same link keeps working."
        confirmLabel="Extend"
        defaultDays="15"
        onConfirm={async (days) => apply(await api.growth.previews.extend(id, days), `${days} more days.`)}
      />

      <ConfirmDialog
        open={dialog === 'convert'}
        onClose={() => setDialog(null)}
        tone="primary"
        title={`Convert ${preview.name} to a real league?`}
        description={`It becomes a customer league on a 14-day trial of the standard plan, shows in Tenants, and keeps everything ${who} changed. Their sign-in link stops: from now on they sign in with their email, and set a password with “Forgot your password?”.`}
        confirmLabel="Convert"
        onConfirm={async () => {
          const { preview: next } = await api.growth.previews.convert(id)
          apply(next, `${preview.name} is a real league now.`)
        }}
      />

      <ConfirmDialog
        open={dialog === 'delete'}
        onClose={() => setDialog(null)}
        title={`Delete the preview of ${preview.name} now?`}
        description={`Its site and everything in it go for good, including what ${who} changed, and the sign-in link stops. This record stays on the lead's history.`}
        confirmLabel="Delete now"
        onConfirm={async () => apply(await api.growth.previews.deleteNow(id), 'The preview is deleted.')}
      />
    </div>
  )
}
